import { useEffect, useMemo, useState } from 'react';
import { executiveSettingsSchema, type AppSettings, type ExecutiveSettings } from '@ace-tracker/shared';
import { Card, Field, SaveBar, TextInput } from './formParts';
import { useSaveSettings } from './useSaveSettings';

interface Draft {
  engineersInScope: string;
  adoptionTargetPercent: string;
  habitualThresholdPercent: string;
  minTeamSize: string;
}

function toDraft(s: ExecutiveSettings): Draft {
  return {
    engineersInScope: s.engineersInScope === undefined ? '' : String(s.engineersInScope),
    adoptionTargetPercent: String(s.adoptionTargetPercent),
    habitualThresholdPercent: String(s.habitualThresholdPercent),
    minTeamSize: String(s.minTeamSize),
  };
}

function toSettings(d: Draft) {
  const whole = (v: string) => (v.trim() === '' ? NaN : Number(v));
  return {
    engineersInScope: d.engineersInScope.trim() === '' ? undefined : Number(d.engineersInScope),
    adoptionTargetPercent: whole(d.adoptionTargetPercent),
    habitualThresholdPercent: whole(d.habitualThresholdPercent),
    minTeamSize: whole(d.minTeamSize),
  };
}

const FIELD_LABELS: Record<string, string> = {
  engineersInScope: 'Engineers in scope',
  adoptionTargetPercent: 'Adoption target',
  habitualThresholdPercent: 'Habitual threshold',
  minTeamSize: 'Minimum team size',
};

const isWhole = (v: string, min: number, max: number, allowEmpty = false) => {
  if (v.trim() === '') return allowEmpty;
  const n = Number(v);
  return Number.isInteger(n) && n >= min && n <= max;
};

export function ExecutiveSettingsForm({ settings }: { settings: AppSettings }) {
  const baseline = settings.executive;
  const [draft, setDraft] = useState<Draft>(() => toDraft(baseline));
  const [validationError, setValidationError] = useState<string>();
  const { save, saving, error, savedAt, reset } = useSaveSettings('executive');
  const baselineJson = useMemo(() => JSON.stringify(toSettings(toDraft(baseline))), [baseline]);

  useEffect(() => setDraft(toDraft(baseline)), [baseline]);

  const dirty = JSON.stringify(toSettings(draft)) !== baselineJson;

  function set(patch: Partial<Draft>) {
    setValidationError(undefined);
    setDraft((d) => ({ ...d, ...patch }));
  }

  function handleSave() {
    const parsed = executiveSettingsSchema.safeParse(toSettings(draft));
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const field = FIELD_LABELS[String(issue.path[issue.path.length - 1])] ?? 'Value';
      setValidationError(`${field}: ${issue.message.startsWith('Expected') ? 'enter a whole number' : issue.message}`);
      return;
    }
    save(parsed.data);
  }

  return (
    <div className="space-y-5">
      <Card
        title="Adoption"
        description="How the Executive view calculates adoption. These settings only change how figures are presented — telemetry is untouched."
      >
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Field
            label="Engineers in scope"
            hint="How many engineers are expected to use AI tools. Leave empty to use everyone seen in retained telemetry (up to the last 90 days)."
            htmlFor="exec-engineers"
          >
            <TextInput
              id="exec-engineers"
              inputMode="numeric"
              placeholder="Developers seen in telemetry"
              className="max-w-[14rem] text-right tabular-nums"
              value={draft.engineersInScope}
              invalid={!isWhole(draft.engineersInScope, 1, 1_000_000, true)}
              onChange={(e) => set({ engineersInScope: e.target.value })}
            />
          </Field>
          <Field label="Adoption target (%)" hint="Drawn as the target line and marker. 1–100." htmlFor="exec-target">
            <TextInput
              id="exec-target"
              inputMode="numeric"
              className="max-w-[8rem] text-right tabular-nums"
              value={draft.adoptionTargetPercent}
              invalid={!isWhole(draft.adoptionTargetPercent, 1, 100)}
              onChange={(e) => set({ adoptionTargetPercent: e.target.value })}
            />
          </Field>
        </div>
      </Card>

      <Card
        title="Depth of use"
        description="A developer counts as a habitual user when they use AI on at least this share of the period's working days (Monday–Friday). At 60%, that's 12 of 20 working days."
      >
        <div className="max-w-xs">
          <Field label="Habitual threshold (% of working days)" hint="1–100." htmlFor="exec-habitual">
            <TextInput
              id="exec-habitual"
              inputMode="numeric"
              className="max-w-[8rem] text-right tabular-nums"
              value={draft.habitualThresholdPercent}
              invalid={!isWhole(draft.habitualThresholdPercent, 1, 100)}
              onChange={(e) => set({ habitualThresholdPercent: e.target.value })}
            />
          </Field>
        </div>
      </Card>

      <Card
        title="Team privacy"
        description="Create teams in Settings → Teams and assign developers in Settings → Developers. Teams with fewer members than this are merged into “Other” on the Executive view and slide, so no individual can be singled out."
      >
        <div className="max-w-xs">
          <Field label="Minimum team size" hint="1–100." htmlFor="exec-min-team">
            <TextInput
              id="exec-min-team"
              inputMode="numeric"
              className="max-w-[8rem] text-right tabular-nums"
              value={draft.minTeamSize}
              invalid={!isWhole(draft.minTeamSize, 1, 100)}
              onChange={(e) => set({ minTeamSize: e.target.value })}
            />
          </Field>
        </div>
      </Card>

      <SaveBar
        dirty={dirty}
        saving={saving}
        error={validationError ?? error}
        savedAt={savedAt}
        audit={settings.audit.executive}
        onSave={handleSave}
        onDiscard={() => {
          setDraft(toDraft(baseline));
          setValidationError(undefined);
          reset();
        }}
      />
    </div>
  );
}
